import { TimestampAttributeName } from "../../../shiftUtils";
import { UserShiftLastUpdateBy } from "../../../types/Shift.types";

/**
 * Enum for audit edit types to avoid magic strings and prevent typos
 */
export enum AuditEditType {
  ORIGINAL = "Original timestamp",
  CREW_MEMBER = "Crew member",
  CLIENT = "Client",
  BLUECREW_SUPPORT = "BlueCrew support",
  CLIENT_SUPPORT = "Client/Support",
}

/**
 * Unified structure for both clock in/out and break edits.
 * This shared structure enables component reuse across different timestamp types.
 *
 * The new UI always displays: start → end (for both clock in/out AND breaks)
 */
export type TransformedPair = {
  /** Type of edit using enum to avoid magic strings */
  editType: AuditEditType;
  /** When this edit was made */
  updateDate?: Date;
  /** Who made this edit */
  updateUser?: UserShiftLastUpdateBy;

  /** Start time (clock in for shift, break start for breaks) */
  start?: Date;
  /** End time (clock out for shift, break end for breaks) */
  end?: Date;
  /** Attributes for start timestamp (compliance icons, edit markers, etc.) */
  startAttributes: Array<TimestampAttributeName>;
  /** Attributes for end timestamp (compliance icons, edit markers, etc.) */
  endAttributes: Array<TimestampAttributeName>;

  /** Whether this entry represents a deleted break (always false for clock in/out) */
  deleted: boolean;

  /** Most recent update time from the relevant timestamps */
  timestampsUpdatedAt?: Date;
  /** Author of the most recent timestamp update */
  timestampsUpdatedBy?: string;
};

/**
 * Special structure to show breaks as duration in minutes only
 */
export type TransformedDuration = {
  /** Type of edit using enum to avoid magic strings */
  editType: AuditEditType;
  /** When this edit was made */
  updateDate?: Date;
  /** Who made this edit */
  updateUser?: UserShiftLastUpdateBy;

  /** Attributes for the whole break (compliance icons, edit markers, etc.) */
  attributes: Array<TimestampAttributeName>;

  /** Break duration in minutes for legacy shifts (used instead of start/end for legacy break display) */
  breakDurationMinutes: number;

  /** Whether this entry represents a deleted break (always false for clock in/out) */
  deleted: boolean;

  /** Most recent update time from the relevant timestamps */
  timestampsUpdatedAt?: Date;
  /** Author of the most recent timestamp update */
  timestampsUpdatedBy?: string;
}

export type TransformedPairOrDuration = TransformedPair|TransformedDuration;

/**
 * Final transformed audit data structure ready for UI components
 */
export type TransformedAuditData = {
  /** History of clock in/out edits, sorted by updateDate descending */
  clockInOut: TransformedPair[];
  /** Array of break histories, one array per break (Break 1, Break 2, etc.) */
  breaks: TransformedPairOrDuration[][];
};
